import * as pdfjsLib from "pdfjs-dist";
import { useCallback, useEffect, useState } from "react";
import { FaSearch } from "react-icons/fa";
import { MdDeleteOutline, MdOutlineEdit } from "react-icons/md";
import pdfToText from "react-pdftotext";
import { useLocation } from "react-router-dom";
import DeleteConfirmationModal from "../DeleteConfirmationModal/DeleteConfirmationModal";
import LoaderSpiner from "../LoaderSpiner";
import styles from "./KnowledgeBase.module.css";

// Set PDF.js worker source
pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

const KnowledgeBaseCard = ({ kb, onEdit, onDelete }) => (
  <div className={styles.card}>
    <h3>{kb.title || "Untitled"}</h3>
    <p>
      {kb.extractedText
        ? kb.extractedText?.slice(0, 80) + "..."
        : "No extracted text available"}
    </p>

    <div className={styles.cardButtons}>
      <button onClick={() => onEdit(kb._id)} className={styles.editButton}>
        <MdOutlineEdit />
      </button>
      <button onClick={() => onDelete(kb._id)} className={styles.deleteButton}>
        <MdDeleteOutline />
      </button>
    </div>
  </div>
);

const KnowledgeBaseModal = ({
  isOpen,
  onClose,
  title,
  setTitle,
  extractedText,
  setExtractedText,
  pdfFile,
  setPdfFile,
  editingId,
  handleSave,
  handleFileChange,
  assignedProjects,
  selectedProject,
  setSelectedProject,
}) => {
  if (!isOpen) return null;
  return (
    <div className={styles.modal}>
      <div className={styles.modalContent}>
        <h2>{editingId ? "Edit Knowledge Base" : "Create Knowledge Base"}</h2>
        <input
          type="text"
          placeholder="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className={styles.input}
        />
        <input
          type="file"
          accept=".pdf"
          onChange={handleFileChange}
          className={styles.fileInput}
        />
        {/* Project Dropdown */}
        <select
          value={selectedProject}
          onChange={(e) => setSelectedProject(e.target.value)}
          className={styles.input}
        >
          <option value="">Select Assigned Project</option>
          {assignedProjects.map((project) => (
            <option key={project._id} value={project._id}>
              {project.projectname || project.name}
            </option>
          ))}
        </select>
        <textarea
          value={extractedText}
          onChange={(e) => setExtractedText(e.target.value)}
          placeholder="Extracted text (editable)"
          className={styles.textarea}
        />
        <div className={styles.modalButtons}>
          <button onClick={handleSave} className={styles.saveButton}>
            Save
          </button>
          <button onClick={onClose} className={styles.cancelButton}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

function formatDeveloperInfo(dev) {
  if (!dev) return "No developer selected";
  return `Name: ${dev.name}, Email: ${dev.email}, Phone: ${dev.phoneNumber}, Region: ${dev.Region}, Status: ${dev.Status}, DeveloperKnowledgeBase: ${dev.developerKnowledgeBase}`;
}

function formatProjectInfo(proj, developer) {
  if (!proj) return "No project selected";
  const developerInfo = developer
    ? formatDeveloperInfo(developer)
    : "No developer assigned";
  return `Name: ${proj.projectname}, Description: ${proj.projectdescription
    }, Location: ${proj.location}, Type: ${proj.type}, Units: ${proj.units
    }, Price Range: ${proj.priceRange}, Launch Date: ${proj.launchDate?.slice(
      0,
      10
    )}, Completion Date: ${proj.completionDate?.slice(
      0,
      10
    )}, ProjectKnowledgeBase: ${proj.projectKnowledgeBase
    }, Assigned Developer: ${developerInfo}`;
}

const KnowledgeBase = () => {
  const location = useLocation();
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [kbToDelete, setKbToDelete] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [extractedText, setExtractedText] = useState("");
  const [title, setTitle] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filteredKnb, setFilteredKnb] = useState([]);
  const [success, setSuccess] = useState("");
  const [assignedProjects, setAssignedProjects] = useState([]);
  const [assignedDevelopers, setAssignedDevelopers] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");

  const BASE_URL =
    "https://prod.toxboxai.com/api/knowledge-base";

  // Fetch knowledge bases with retry logic
  const fetchKnowledgeBases = useCallback(
    async (retryCount = 3, delay = 1000) => {
      const token = localStorage.getItem("token");
      const creatorId = localStorage.getItem("userId");

      if (!token || !creatorId) {
        setError(
          "Authentication token or creator ID is missing. Please log in."
        );
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError("");
      console.log(`Fetching knowledge bases for creatorId: ${creatorId}`);

      for (let attempt = 1; attempt <= retryCount; attempt++) {
        try {
          const response = await fetch(
            `${BASE_URL}/get-knowledgeBase-by-creator/${creatorId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (!response.ok) {
            throw new Error(
              `HTTP error ${response.status}: ${response.statusText}`
            );
          }

          const data = await response.json();
          console.log(`Fetched knowledge bases:`, data);

          // Ensure data is an array
          const knowledgeBaseArray = Array.isArray(data.knowledgeBases)
            ? data.knowledgeBases
            : [];
          setKnowledgeBases(knowledgeBaseArray);
          setFilteredKnb(knowledgeBaseArray);
          setIsLoading(false);
          return;
        } catch (error) {
          console.error(`Attempt ${attempt} failed:`, error);
          if (attempt < retryCount) {
            await new Promise((resolve) => setTimeout(resolve, delay));
            continue;
          }
          setError(
            `Failed to fetch knowledge bases after ${retryCount} attempts: ${error.message}`
          );
          setKnowledgeBases([]);
          setIsLoading(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (search.trim() === "") {
        setFilteredKnb(knowledgeBases);
      } else {
        const results = knowledgeBases.filter((knb) =>
          knb.title.toLowerCase().includes(search.toLowerCase())
        );
        setFilteredKnb(results);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [search, knowledgeBases]);

  useEffect(() => {
    console.log(`Component mounted, triggering fetch`);
    setKnowledgeBases([]);
    fetchKnowledgeBases();
  }, [fetchKnowledgeBases]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    // Fetch assigned projects and developers
    Promise.all([
      fetch(
        "https://prod.toxboxai.com/api/campaign/assigned-projects",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      ).then((res) => res.json()),
      fetch(
        "https://prod.toxboxai.com/api/campaign/assigned-developers",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      ).then((res) => res.json()),
    ])
      .then(([projectData, developerData]) => {
        if (projectData.success && Array.isArray(projectData.data?.projects)) {
          setAssignedProjects(projectData.data.projects);
        } else if (Array.isArray(projectData.projects)) {
          setAssignedProjects(projectData.projects);
        }

        if (
          developerData.success &&
          Array.isArray(developerData.data?.developers)
        ) {
          setAssignedDevelopers(developerData.data.developers);
        } else if (Array.isArray(developerData.developers)) {
          setAssignedDevelopers(developerData.developers);
        }
      })
      .catch((err) =>
        console.error("Error fetching projects/developers:", err)
      );
  }, []);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setPdfFile(file);
      try {
        const text = await pdfToText(file);
        setExtractedText(text);
      } catch (error) {
        console.error("Failed to extract text from PDF:", error);
        setError("Failed to extract text from PDF.");
        setExtractedText("");
      }
    }
  };

  const handleSave = async () => {
    setError("");
    const token = localStorage.getItem("token");
    if (!token) {
      setError("Authentication token is missing. Please log in.");
      return;
    }

    try {
      if (!title.trim()) {
        throw new Error("Title is required");
      }

      const selectedProjectObj = assignedProjects.find(
        (proj) => proj._id === selectedProject
      );
      const selectedDeveloper = selectedProjectObj
        ? assignedDevelopers.find(
          (dev) => dev._id === selectedProjectObj.developerId
        )
        : null;

      const projectInfo = formatProjectInfo(
        selectedProjectObj,
        selectedDeveloper
      );

      const combinedExtractedText =
        `Project Information:\n${projectInfo}\n\n` +
        `Extracted Text:\n${extractedText}`;

      const data = {
        title,
        extractedText: combinedExtractedText,
        assignedProjectId: selectedProject,
        assignedDeveloperId: selectedDeveloper?._id || null,
      };
      console.log("Sending data:", data);

      const response = await fetch(
        editingId
          ? `${BASE_URL}/update-knowldgeBase/${editingId}`
          : `${BASE_URL}/create-knowldgeBase`,
        {
          method: editingId ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error ${response.status}`);
      }

      const responseData = await response.json();
      console.log("Received response:", responseData);

      setSuccess(responseData.message || "Knowledge base created successfully");
      setTimeout(() => setSuccess(""), 3000);

      setIsModalOpen(false);
      setPdfFile(null);
      setExtractedText("");
      setTitle("");
      setEditingId(null);
      setSelectedProject("");
      fetchKnowledgeBases();
    } catch (error) {
      console.error("Error saving knowledge base:", error);
      setError(error.message || "Failed to save knowledge base.");
    }
  };

  const handleEdit = async (id) => {
    const token = localStorage.getItem("token");
    if (!token) {
      setError("Authentication token is missing. Please log in.");
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/get-knowldgeBase-by-id/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }

      const data = await response.json();
      console.log("Fetched knowledge base for edit:", data);

      // Access the nested knowledgeBase object
      const kbData = data.knowledgeBase || data;

      setTitle(kbData.title || "");
      setExtractedText(kbData.extractedText || "");
      setEditingId(id);
      setSelectedProject(kbData.assignedProjectId || "");
      setIsModalOpen(true);
    } catch (error) {
      console.error("Error fetching knowledge base:", error);
      setError("Failed to fetch knowledge base.");
    }
  };

  const handleDelete = (id) => {
    const kb = knowledgeBases.find((k) => k._id === id);
    setKbToDelete(kb);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!kbToDelete) return;

    const token = localStorage.getItem("token");
    if (!token) {
      setError("Authentication token is missing. Please log in.");
      setIsDeleteModalOpen(false);
      setKbToDelete(null);
      return;
    }

    try {
      const response = await fetch(
        `${BASE_URL}/delete-knowldgeBase/${kbToDelete._id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }

      fetchKnowledgeBases();
      setIsDeleteModalOpen(false);
      setKbToDelete(null);
    } catch (error) {
      console.error("Error deleting knowledge base:", error);
      setError("Failed to delete knowledge base.");
      setIsDeleteModalOpen(false);
      setKbToDelete(null);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.TopContainer}>
        <span className={styles.SearchInputIcon}>
          <FaSearch />
        </span>
        <input
          className={styles.SearchInput}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button
          className={styles.addButton}
          onClick={() => {
            setIsModalOpen(true);
            setEditingId(null);
            setTitle("");
            setExtractedText("");
            setPdfFile(null);
            setError("");
            setSelectedProject("");
          }}
        >
          Add Knowledge Base
        </button>
      </div>

      {success && <p className={styles.success}>{success}</p>}
      {error && <p className={styles.error}>{error}</p>}
      {isLoading && <LoaderSpiner />}

      <KnowledgeBaseModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={title}
        setTitle={setTitle}
        extractedText={extractedText}
        setExtractedText={setExtractedText}
        pdfFile={pdfFile}
        setPdfFile={setPdfFile}
        editingId={editingId}
        handleSave={handleSave}
        handleFileChange={handleFileChange}
        assignedProjects={assignedProjects}
        selectedProject={selectedProject}
        setSelectedProject={setSelectedProject}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setKbToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={kbToDelete?.title || "Untitled"}
      />

      <div className={styles.cardContainer}>
        {isLoading ? (
          <LoaderSpiner />
        ) : knowledgeBases.length === 0 ? (
          <p>No knowledge bases found.</p>
        ) : (
          <>
            {filteredKnb.map((kb) => (
              <KnowledgeBaseCard
                key={kb._id}
                kb={kb}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default KnowledgeBase;
